import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnitCode, type Quantity } from '~/graphql/graphql.ts';
import { EditCellForm, type EditCellFormProps } from '~/components/ui/table';
import {
  InlineEditButton,
  MissingValueButton,
} from '~/components/ui/buttons/InlineValueButtons';
import { VehiclePayloadLabel } from '~/components/ui/VehiclePayloadLabel';
import type { TransportMode } from '~/hooks/useTransportState';
import { getPlatformProductDimensions } from '~/utils/order';
import { unitsPerPiece } from '~/utils/unitConversion';
import {
  capacityMessageParams,
  parseCapacityInput,
  wholeCapacityMessageParams,
} from '~/utils/vehicleCapacityBounds';
import type { BundleProductLengthLike } from '~/components/ui/bundleQuantity';

type VehicleCapacityEditorProps = {
  platformProductsLength: BundleProductLengthLike;
  quantityUnit: UnitCode;
  vehiclePayload?: Quantity | null;
  vehicleLabel?: TransportMode;
  onCapacitySubmit?: (value: number) => void;
  disabled?: boolean;
};

export function VehicleCapacityEditor({
  platformProductsLength,
  quantityUnit,
  vehiclePayload,
  vehicleLabel,
  onCapacitySubmit,
  disabled = false,
}: Readonly<VehicleCapacityEditorProps>) {
  const { t } = useTranslation('cart');
  const [isEditingCapacity, setIsEditingCapacity] = useState(false);
  const hasCapacity = vehiclePayload != null && vehiclePayload.value > 0;

  const handleCapacitySubmit: EditCellFormProps['onSubmit'] = (raw, refuse) => {
    const parsed = parseCapacityInput(raw, quantityUnit);
    if (!parsed.valid) {
      refuse(
        quantityUnit === UnitCode.Pcs
          ? t('cart.capacity_error_whole', wholeCapacityMessageParams)
          : t('cart.capacity_error', capacityMessageParams)
      );
      return;
    }
    onCapacitySubmit?.(parsed.value);
    setIsEditingCapacity(false);
  };

  if (!vehicleLabel || !onCapacitySubmit) return null;

  const editor = isEditingCapacity && (
    <EditCellForm
      defaultValue={hasCapacity ? vehiclePayload.value : ''}
      editableType="number"
      onSubmit={handleCapacitySubmit}
      onCancel={() => setIsEditingCapacity(false)}
      popupPosition="above"
      testIds={{
        input: 'capacity-input',
        submit: 'capacity-submit',
        cancel: 'capacity-cancel',
      }}
    />
  );

  if (hasCapacity) {
    const { widthInches, thicknessInches, lengthInches } =
      getPlatformProductDimensions(
        platformProductsLength.platformProduct,
        platformProductsLength.length,
        platformProductsLength.isNominal
      );
    let piecesCount: number | undefined;
    if (
      quantityUnit !== UnitCode.Pcs &&
      lengthInches > 0 &&
      widthInches > 0 &&
      thicknessInches > 0
    ) {
      try {
        piecesCount =
          vehiclePayload.value /
          unitsPerPiece(
            vehiclePayload.unit,
            lengthInches,
            widthInches,
            thicknessInches
          );
      } catch {
        piecesCount = undefined;
      }
    }
    return (
      <div className="relative flex items-center gap-1">
        <VehiclePayloadLabel
          quantity={vehiclePayload}
          vehicleLabel={vehicleLabel}
          piecesCount={piecesCount}
        />
        <InlineEditButton
          label={t('cart.edit_capacity')}
          testId="edit-capacity-button"
          onClick={() => setIsEditingCapacity(true)}
          disabled={disabled}
        />
        {editor}
      </div>
    );
  }

  return (
    <div className="relative">
      <MissingValueButton
        label={t('cart.add_capacity', {
          unit: quantityUnit,
          vehicle: vehicleLabel,
        })}
        testId="add-capacity-button"
        onClick={() => setIsEditingCapacity(true)}
        disabled={disabled}
      />
      {editor}
    </div>
  );
}
